import { useEffect, useRef, useState, useCallback } from "react";
import { Phone, Loader2, CheckCircle2, AlertCircle, PhoneOff, X, Minimize2, LogIn, ExternalLink, RefreshCw } from "lucide-react";
import ENV from "@/lib/env";

declare global {
  interface Window {
    connect: any;
    isConnectInitialized?: boolean;
  }
}

interface ConnectCCPWidgetProps {
  sidebarExpanded?: boolean;
  userEmail?: string;
}

const STREAMS_SCRIPT_URL = "https://unpkg.com/amazon-connect-streams@2.18.0/release/connect-streams.js";
const STREAMS_SCRIPT_ID  = "amazon-connect-streams";
const DEFAULT_CONNECT_CCP_URL   = "https://monitorhealth.my.connect.aws/ccp-v2/";
const DEFAULT_CONNECT_LOGIN_URL = "https://monitorhealth.my.connect.aws/connect/login";

function loadStreamsScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.getElementById(STREAMS_SCRIPT_ID) && window.connect) {
      resolve();
      return;
    }
    const existing = document.getElementById(STREAMS_SCRIPT_ID);
    if (existing) {
      existing.remove();
    }
    const s = document.createElement("script");
    s.id = STREAMS_SCRIPT_ID;
    s.src = STREAMS_SCRIPT_URL;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Failed to load amazon-connect-streams"));
    document.head.appendChild(s);
  });
}

type Status = "idle" | "loading" | "connected" | "error" | "auth_needed";

export function ConnectCCPWidget({ sidebarExpanded: _sidebarExpanded = true, userEmail = "" }: ConnectCCPWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const loginPopupRef = useRef<Window | null>(null);
  const [status, setStatus] = useState<Status>("idle");
  const [showPhone, setShowPhone] = useState(false);
  const [authWindowOpen, setAuthWindowOpen] = useState(false);

  // Preload Streams script on mount
  useEffect(() => {
    loadStreamsScript().catch((err) => {
      console.warn("Failed to pre-load Amazon Connect Streams script:", err);
    });
  }, []);

  const ccpUrl: string =
    (ENV as any).VITE_CONNECT_CCP_URL ||
    (window as any)._env_?.VITE_CONNECT_CCP_URL ||
    DEFAULT_CONNECT_CCP_URL;

  const ssoUrl: string =
    (ENV as any).VITE_CONNECT_LOGIN_URL ||
    (window as any)._env_?.VITE_CONNECT_LOGIN_URL ||
    DEFAULT_CONNECT_LOGIN_URL;

  // Login URL points to the authentication portal (NOT /ccp-v2/softphone)
  const buildLoginUrl = useCallback((): string => {
    let base = ssoUrl || DEFAULT_CONNECT_LOGIN_URL;
    if (!base || base.startsWith("/")) {
      base = DEFAULT_CONNECT_LOGIN_URL;
    }

    try {
      const u = new URL(base);
      if (userEmail && !u.searchParams.has("username")) {
        u.searchParams.set("username", userEmail.toLowerCase().trim());
      }
      return u.toString();
    } catch {
      return `${DEFAULT_CONNECT_LOGIN_URL}${userEmail ? `?username=${encodeURIComponent(userEmail.toLowerCase().trim())}` : ''}`;
    }
  }, [ssoUrl, userEmail]);

  // Clean close of login popup
  const closeLoginPopup = useCallback(() => {
    if (loginPopupRef.current && !loginPopupRef.current.closed) {
      try {
        loginPopupRef.current.close();
      } catch (e) {
        console.warn("Could not close login popup:", e);
      }
      loginPopupRef.current = null;
    }
    setAuthWindowOpen(false);
  }, []);

  // Clean disconnect: terminates WebRTC and releases microphone
  const handleDisconnect = useCallback(() => {
    try {
      if (window.connect?.core?.terminate) {
        window.connect.core.terminate();
      }
    } catch (e) {
      console.error("Failed to terminate connect session:", e);
    }
    closeLoginPopup();
    (window as any).isConnectInitialized = false;
    setStatus("idle");
    setShowPhone(false);
  }, [closeLoginPopup]);

  // Initialize CCP purely framed (loginPopup: false) so no secondary softphone window is created
  const initFramedCCP = useCallback(async () => {
    if (!ccpUrl) return;
    setStatus("loading");

    try {
      if (!window.connect) {
        await loadStreamsScript();
      }
      const container = containerRef.current;
      if (!container) {
        setStatus("error");
        return;
      }

      // If already initialized, try checking agent or reloading iframe src
      if ((window as any).isConnectInitialized) {
        try {
          if (window.connect?.agent?.initialized) {
            setStatus("connected");
            return;
          }
          const iframe = container.querySelector("iframe");
          if (iframe) {
            iframe.src = ccpUrl;
            return;
          }
        } catch {}
      }

      // Clear any stale children inside container
      container.innerHTML = "";

      // Initialize framed CCP
      window.connect.core.initCCP(container, {
        ccpUrl              : ccpUrl,
        loginUrl            : buildLoginUrl(),
        loginPopup          : true,
        loginPopupAutoClose : true,
        loginOptions        : {
          autoClose: true,
          width: 480,
          height: 640,
        },
        softphone           : { allowFramedSoftphone: true, disableRingtone: false },
        pageOptions         : {
          enableAudioDeviceSettings: true,
          enablePhoneTypeSettings  : true,
        },
      });

      (window as any).isConnectInitialized = true;

      // Subscribe to ACK_TIMEOUT (fires when framed CCP cannot authenticate)
      try {
        window.connect.core.getEventBus?.()?.subscribe(window.connect.EventType.ACK_TIMEOUT, () => {
          console.warn("⚠️ Connect ACK_TIMEOUT — auth needed");
          setStatus("auth_needed");
        });
      } catch (e) {
        console.warn("Could not subscribe to ACK_TIMEOUT:", e);
      }

      // Safety timeout: if agent is not connected within 3.5s, switch to auth_needed so user can sign in
      const authTimeout = setTimeout(() => {
        try {
          if (!window.connect?.agent?.initialized) {
            console.warn("⚠️ Connect agent not ready after 3.5s timeout — switching to auth_needed");
            setStatus((prev) => (prev === "loading" ? "auth_needed" : prev));
          }
        } catch {}
      }, 3500);

      // Register agent listener - triggers as soon as agent is authenticated
      window.connect.agent((agent: any) => {
        clearTimeout(authTimeout);
        console.log("✅ Connect agent ready in framed softphone:", agent.getName?.() ?? "");
        setStatus("connected");
        closeLoginPopup();
      });

      // Contact events
      window.connect.contact((contact: any) => {
        console.log("📞 Amazon Connect contact event:", contact);
        setShowPhone(true);

        contact.onConnecting(() => {
          console.log("📞 Call connecting...");
          setShowPhone(true);
        });

        contact.onConnected(() => {
          console.log("📞 Call connected!");
          setShowPhone(true);
        });

        contact.onEnded(() => {
          console.log("📞 Call ended.");
        });
      });

      window.connect.core.onInitialized(() => {
        console.log("✅ Connect core initialized");
        try {
          if (window.connect?.agent?.initialized) {
            clearTimeout(authTimeout);
            setStatus("connected");
            closeLoginPopup();
          }
        } catch {}
      });

      window.connect.core.onAuthFail(() => {
        clearTimeout(authTimeout);
        console.warn("⚠️ Connect auth failed - login required");
        setStatus("auth_needed");
      });

      window.connect.core.onAccessDenied(() => {
        clearTimeout(authTimeout);
        console.warn("⚠️ Connect access denied - login required or origin whitelist needed");
        setStatus("auth_needed");
      });

    } catch (err) {
      console.error("ConnectCCPWidget init error:", err);
      setStatus("error");
    }
  }, [ccpUrl, buildLoginUrl, closeLoginPopup]);

  // Open the temporary login window (ONLY for auth, NOT for softphone)
  const openLoginWindow = useCallback(() => {
    // If popup already open, focus it
    if (loginPopupRef.current && !loginPopupRef.current.closed) {
      try {
        loginPopupRef.current.focus();
        setAuthWindowOpen(true);
        return;
      } catch {}
    }

    const loginUrl = buildLoginUrl();
    if (!loginUrl) return;

    setAuthWindowOpen(true);
    const popup = window.open(
      loginUrl,
      "ConnectLoginPortal",
      "width=480,height=640,status=no,toolbar=no,menubar=no,resizable=yes,scrollbars=yes"
    );
    loginPopupRef.current = popup;

    if (!popup || popup.closed || typeof popup.closed === "undefined") {
      alert("Please allow popups for this site to log in to Amazon Connect.");
      setAuthWindowOpen(false);
      return;
    }

    try {
      popup.focus();
    } catch {}

    // Poll to detect when user finishes logging in
    const poll = setInterval(() => {
      // 1. Check if agent became initialized
      try {
        if (window.connect?.agent?.initialized) {
          clearInterval(poll);
          closeLoginPopup();
          setStatus("connected");
          return;
        }
      } catch {}

      // 2. Check if popup was closed
      if (popup.closed) {
        clearInterval(poll);
        loginPopupRef.current = null;
        setAuthWindowOpen(false);

        // Refresh the framed iframe now that cookies are stored
        const iframe = containerRef.current?.querySelector("iframe");
        if (iframe) {
          iframe.src = ccpUrl;
        } else {
          initFramedCCP();
        }

        // Re-check agent status after brief pause for cookie propagation
        setTimeout(() => {
          try {
            if (window.connect?.agent?.initialized) {
              setStatus("connected");
            } else {
              setStatus("auth_needed");
            }
          } catch {
            setStatus("auth_needed");
          }
        }, 1500);
      }
    }, 800);
  }, [buildLoginUrl, closeLoginPopup, ccpUrl, initFramedCCP]);

  // Main connect trigger when user wants to open/connect
  const handleOpenSoftphone = useCallback(() => {
    setShowPhone(true);
    const isAgentInit = !!(window.connect?.agent?.initialized);
    if (!isAgentInit) {
      if (!(window as any).isConnectInitialized || status === "idle" || status === "error") {
        initFramedCCP();
      }
      if (status === "auth_needed") {
        openLoginWindow();
      }
    } else {
      setStatus("connected");
    }
  }, [status, initFramedCCP, openLoginWindow]);

  // Listen for external triggers to open the phone (e.g. from Appointments Call Now)
  useEffect(() => {
    const handleExternalOpen = () => {
      setShowPhone(true);
      const isAgentInit = !!(window.connect?.agent?.initialized);
      if (!isAgentInit) {
        // Automatically pop open the login window when a call is initiated!
        openLoginWindow();
        if (!(window as any).isConnectInitialized) {
          initFramedCCP();
        }
      } else {
        setStatus("connected");
      }
    };

    window.addEventListener("connect:open-phone", handleExternalOpen);
    return () => window.removeEventListener("connect:open-phone", handleExternalOpen);
  }, [status, initFramedCCP, openLoginWindow]);

  // Auto-init framed CCP on mount if container is available
  useEffect(() => {
    if (ccpUrl && !((window as any).isConnectInitialized)) {
      initFramedCCP();
    }
  }, [ccpUrl, initFramedCCP]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      closeLoginPopup();
    };
  }, [closeLoginPopup]);

  // CCP URL not configured — return null
  if (!ccpUrl) {
    return null;
  }

  const isConnected  = status === "connected";
  const isLoading    = status === "loading";
  const isError      = status === "error";
  const isAuthNeeded = status === "auth_needed";
  const isIdle       = status === "idle";

  return (
    <>
      {/*
        SIDE BOTTOM MODAL SHEET BOX:
        Hosts the ONE AND ONLY Amazon Connect softphone iframe.
        - loginPopup: false ensures NO secondary window runs CCP.
        - Positioned docked at bottom-right corner (bottom: 24px, right: 24px).
        - When minimized (showPhone === false), moved off-screen without unmounting,
          preserving WebRTC connection and microphone state.
      */}
      <div
        id="side-bottom-softphone-sheet"
        style={
          showPhone
            ? {
                position: "fixed",
                bottom: "24px",
                right: "24px",
                width: "380px",
                height: "600px",
                zIndex: 9999,
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(0, 0, 0, 0.12)",
                borderRadius: "20px",
                overflow: "hidden",
                border: "2px solid #1a5c38",
                backgroundColor: "#ffffff",
                display: "flex",
                flexDirection: "column",
                animation: "fadeInUp 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
              }
            : {
                position: "fixed",
                left: "-9999px",
                top: 0,
                width: "1px",
                height: "1px",
                overflow: "hidden",
                pointerEvents: "none",
              }
        }
      >
        {/* Top Control Bar of Side Bottom Modal Sheet Box */}
        <div
          style={{
            height: "46px",
            backgroundColor: "#1a5c38",
            color: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 14px",
            fontSize: "13px",
            fontWeight: 600,
            userSelect: "none",
            flexShrink: 0,
            borderBottom: "1px solid rgba(255,255,255,0.12)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span
              style={{
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                backgroundColor: isConnected ? "#10b981" : isLoading ? "#f59e0b" : "#ef4444",
                boxShadow: isConnected ? "0 0 8px #10b981" : "none",
              }}
            />
            <span className="tracking-tight">Amazon Connect Softphone</span>
            <span
              style={{
                fontSize: "10px",
                padding: "2px 7px",
                borderRadius: "9999px",
                backgroundColor: isConnected ? "rgba(16, 185, 129, 0.2)" : "rgba(245, 158, 11, 0.2)",
                color: isConnected ? "#a7f3d0" : "#fde68a",
                fontWeight: 500,
              }}
            >
              {isConnected ? "Ready" : isLoading ? "Connecting…" : "Login Needed"}
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            {!isConnected && (
              <button
                type="button"
                onClick={openLoginWindow}
                style={{
                  background: "rgba(255,255,255,0.2)",
                  border: "none",
                  color: "#ffffff",
                  cursor: "pointer",
                  padding: "4px 8px",
                  fontSize: "11px",
                  borderRadius: "6px",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
                title="Log in to Amazon Connect"
              >
                <LogIn style={{ width: "12px", height: "12px" }} />
                <span>Log in</span>
              </button>
            )}

            <button
              type="button"
              onClick={() => setShowPhone(false)}
              title="Minimize to bottom-right button"
              style={{
                background: "transparent",
                border: "none",
                color: "#e2e8f0",
                cursor: "pointer",
                padding: "4px 6px",
                fontSize: "11px",
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                gap: "3px",
              }}
            >
              <Minimize2 style={{ width: "13px", height: "13px" }} />
              <span className="text-[10px]">Minimize</span>
            </button>

            {isConnected ? (
              <button
                type="button"
                onClick={handleDisconnect}
                title="Disconnect softphone and turn off microphone"
                style={{
                  background: "#dc2626",
                  border: "none",
                  color: "#ffffff",
                  cursor: "pointer",
                  padding: "4px 8px",
                  fontSize: "11px",
                  borderRadius: "6px",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <PhoneOff style={{ width: "12px", height: "12px" }} />
                <span>Disconnect</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setShowPhone(false)}
                title="Close softphone sheet"
                style={{
                  background: "rgba(255,255,255,0.15)",
                  border: "none",
                  color: "#ffffff",
                  cursor: "pointer",
                  padding: "4px 6px",
                  fontSize: "11px",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <X style={{ width: "13px", height: "13px" }} />
              </button>
            )}
          </div>
        </div>

        {/*
          CONTAINER WHERE AMAZON CONNECT STREAMS INSERTS THE FRAMED CCP:
          The iframe completely fills this div below the header.
        */}
        <div
          ref={containerRef}
          id="ccp-frame-container"
          style={{
            flex: 1,
            width: "100%",
            height: "calc(100% - 46px)",
            position: "relative",
            backgroundColor: "#ffffff",
            overflow: "hidden",
          }}
        />

        {/* OVERLAY: Loading state */}
        {showPhone && isLoading && !authWindowOpen && (
          <div
            style={{
              position: "absolute",
              top: "46px",
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.96)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              zIndex: 20,
              padding: "24px",
              textAlign: "center",
            }}
          >
            <Loader2 style={{ width: "36px", height: "36px", color: "#1a5c38", animation: "spin 1s linear infinite" }} />
            <p style={{ fontSize: "14px", fontWeight: 600, color: "#1e293b", margin: 0 }}>
              Connecting to Amazon Connect…
            </p>
            <p style={{ fontSize: "12px", color: "#64748b", margin: 0, maxWidth: "260px" }}>
              Loading softphone directly into this modal sheet.
            </p>
            <button
              type="button"
              onClick={openLoginWindow}
              style={{
                marginTop: "6px",
                backgroundColor: "#1a5c38",
                color: "#ffffff",
                border: "none",
                borderRadius: "8px",
                padding: "8px 16px",
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
              }}
            >
              <LogIn style={{ width: "13px", height: "13px" }} />
              <span>Open Login Window</span>
            </button>
          </div>
        )}

        {/* OVERLAY: Waiting for external sign-in to complete */}
        {showPhone && authWindowOpen && (
          <div
            style={{
              position: "absolute",
              top: "46px",
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.98)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "14px",
              zIndex: 25,
              padding: "24px",
              textAlign: "center",
            }}
          >
            <div style={{ width: "50px", height: "50px", borderRadius: "50%", backgroundColor: "#ecfdf5", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ExternalLink style={{ width: "24px", height: "24px", color: "#1a5c38" }} />
            </div>
            <div>
              <p style={{ fontSize: "15px", fontWeight: 700, color: "#0f172a", margin: "0 0 6px 0" }}>
                Logging In to AWS
              </p>
              <p style={{ fontSize: "12px", color: "#64748b", margin: 0, lineHeight: 1.5, maxWidth: "290px" }}>
                Please complete your sign-in in the authentication window. Once finished, click below to activate your softphone right here:
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                closeLoginPopup();
                const iframe = containerRef.current?.querySelector("iframe");
                if (iframe) {
                  iframe.src = ccpUrl;
                } else {
                  initFramedCCP();
                }
                setStatus("connected");
              }}
              style={{
                backgroundColor: "#1a5c38",
                color: "#ffffff",
                border: "none",
                borderRadius: "10px",
                padding: "10px 20px",
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
              }}
            >
              <CheckCircle2 style={{ width: "14px", height: "14px" }} />
              <span>✓ I Have Signed In</span>
            </button>
          </div>
        )}

        {/* OVERLAY: Login Required / Auth Needed */}
        {showPhone && (isAuthNeeded || (isIdle && !isConnected && !isLoading)) && !authWindowOpen && (
          <div
            style={{
              position: "absolute",
              top: "46px",
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "#ffffff",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "14px",
              zIndex: 20,
              padding: "32px 24px",
              textAlign: "center",
            }}
          >
            <div style={{ width: "54px", height: "54px", borderRadius: "50%", backgroundColor: "#ecfdf5", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Phone style={{ width: "26px", height: "26px", color: "#1a5c38" }} />
            </div>
            <div>
              <p style={{ fontSize: "15px", fontWeight: 700, color: "#0f172a", margin: "0 0 6px 0" }}>
                Sign In to Softphone
              </p>
              <p style={{ fontSize: "12px", color: "#64748b", margin: 0, lineHeight: 1.5, maxWidth: "280px" }}>
                Authenticate with Amazon Connect to make and receive calls. The softphone will be active right here inside this sheet.
              </p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", width: "100%", maxWidth: "260px" }}>
              <button
                type="button"
                onClick={openLoginWindow}
                style={{
                  backgroundColor: "#1a5c38",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "10px",
                  padding: "10px 22px",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "6px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                }}
              >
                <LogIn style={{ width: "15px", height: "15px" }} />
                <span>Log in to Amazon Connect</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  const iframe = containerRef.current?.querySelector("iframe");
                  if (iframe) {
                    iframe.src = ccpUrl;
                  } else {
                    initFramedCCP();
                  }
                }}
                style={{
                  backgroundColor: "transparent",
                  color: "#64748b",
                  border: "1px solid #e2e8f0",
                  borderRadius: "10px",
                  padding: "8px 16px",
                  fontSize: "11px",
                  fontWeight: 500,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                }}
              >
                <RefreshCw style={{ width: "12px", height: "12px" }} />
                <span>Reload Softphone</span>
              </button>
            </div>
          </div>
        )}

        {/* OVERLAY: Error state */}
        {showPhone && isError && !authWindowOpen && (
          <div
            style={{
              position: "absolute",
              top: "46px",
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "#ffffff",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              zIndex: 20,
              padding: "24px",
              textAlign: "center",
            }}
          >
            <AlertCircle style={{ width: "36px", height: "36px", color: "#ef4444" }} />
            <p style={{ fontSize: "14px", fontWeight: 600, color: "#1e293b", margin: 0 }}>
              Connection Failed
            </p>
            <p style={{ fontSize: "12px", color: "#64748b", margin: 0, maxWidth: "260px" }}>
              Please ensure this domain is approved in Amazon Connect console.
            </p>
            <button
              type="button"
              onClick={initFramedCCP}
              style={{
                backgroundColor: "#1a5c38",
                color: "#ffffff",
                border: "none",
                borderRadius: "8px",
                padding: "8px 18px",
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Retry Connection
            </button>
          </div>
        )}
      </div>

      {/*
        FLOATING BUTTON AT THE SIDE BOTTOM (when minimized / closed):
        Allows user to reopen the side-bottom modal sheet at any time!
      */}
      {!showPhone && (
        <div className="fixed bottom-6 right-6 z-[9998] flex items-center">
          <button
            type="button"
            id="connect-softphone-floating-trigger"
            onClick={handleOpenSoftphone}
            className="group flex items-center gap-2.5 px-4 py-3 rounded-full bg-gradient-to-r from-[#1a5c38] to-[#14482c] hover:from-[#154c2e] hover:to-[#0f3620] text-white shadow-2xl hover:shadow-emerald-900/40 border border-emerald-400/40 hover:scale-[1.03] active:scale-[0.98] transition-all duration-200 cursor-pointer"
            title={isConnected ? "Open Amazon Connect Softphone (Connected)" : "Open Softphone & Log in"}
          >
            <div className="relative flex items-center justify-center">
              <Phone className={`w-4 h-4 ${isLoading ? "animate-spin" : isConnected ? "text-emerald-300" : "text-amber-300"}`} />
              <span
                className={`absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full ring-2 ring-[#1a5c38] ${
                  isConnected
                    ? "bg-emerald-400 animate-pulse"
                    : isLoading
                    ? "bg-amber-400 animate-ping"
                    : isError
                    ? "bg-red-400"
                    : "bg-amber-400"
                }`}
              />
            </div>
            <div className="flex flex-col text-left">
              <span className="text-xs font-bold tracking-tight">
                {isConnected ? "Softphone" : "Amazon Connect"}
              </span>
              <span className="text-[10px] text-emerald-200/90 leading-none">
                {isConnected ? "Online • Ready" : isLoading ? "Connecting…" : "Click to Open / Login"}
              </span>
            </div>
          </button>
        </div>
      )}

      {/*
        SIDEBAR WIDGET UI (inside the left navigation bar) — Hidden per user request:
        The softphone is now managed exclusively via the side-bottom modal sheet box
        and the floating launcher button at the bottom-right.
      */}
    </>
  );
}

export default ConnectCCPWidget;
